import { ArrowLeft, ArrowRight } from "lucide-react";
import { forwardRef, useImperativeHandle, useReducer } from "react";

import { useAppLanguage } from "~/components/form/language/use-app-language";
import { cn } from "~/util/cn";
import { useWatchChange } from "~/util/hooks";

const now = new Date();
const thisYear = now.getFullYear();
const thisMonth = now.getMonth();

enum ActionType {
  SetYear,
  SetMonth,
  IncrementYear,
  DecrementYear,
  Reset,
}

type Action =
  | { type: ActionType.SetYear; payload: number }
  | { type: ActionType.SetMonth; payload: number }
  | { type: ActionType.IncrementYear }
  | { type: ActionType.DecrementYear }
  | { type: ActionType.Reset };

interface State {
  year: number;
  month: number | null;
}

function dateReducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionType.SetYear:
      return { ...state, year: action.payload };
    case ActionType.SetMonth:
      if (state.month === action.payload) {
        return { ...state, month: null };
      }
      return { ...state, month: action.payload };
    case ActionType.IncrementYear:
      return { ...state, year: state.year + 1 };
    case ActionType.DecrementYear:
      return { ...state, year: state.year - 1 };
    case ActionType.Reset:
      return { year: thisYear, month: null };
    default:
      return state;
  }
}

type DatePickerMonthYearProps = {
  onSelect: (payload: {
    year: number;
    month: number | null;
    monthLong: string | null;
  }) => void;
  onUnselect: () => void;
};

export type DatePickerMonthYearRef = {
  reset: () => void;
};

export const DatePickerMonthYear = forwardRef<
  DatePickerMonthYearRef,
  DatePickerMonthYearProps
>(({ onSelect, onUnselect }, ref) => {
  const language = useAppLanguage();
  const months = Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(language.appLanguage, { month: "long" }).format(
      new Date(0, i),
    ),
  );

  const [state, dispatch] = useReducer(dateReducer, {
    year: thisYear,
    month: null,
  });

  useWatchChange(state, (newState) => {
    if (newState.month === null) {
      onUnselect();
    } else {
      onSelect({
        year: newState.year,
        month: newState.month,
        monthLong: months[newState.month],
      });
    }
  });

  useImperativeHandle(
    ref,
    () => ({
      reset: () => dispatch({ type: ActionType.Reset }),
    }),
    [],
  );

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <button
          title="prev"
          onClick={() => dispatch({ type: ActionType.DecrementYear })}
          className="btn-ghost btn-square btn-sm btn"
        >
          <ArrowLeft width={16} />
        </button>
        <div className="flex flex-nowrap items-center gap-1 font-medium">
          {state.year}
        </div>
        <button
          title="next"
          onClick={() => dispatch({ type: ActionType.IncrementYear })}
          className="btn-ghost btn-square btn-sm btn"
        >
          <ArrowRight width={16} />
        </button>
      </div>

      <ul className="grid grid-cols-2 gap-0.5 sm:grid-cols-3 sm:gap-1.5">
        {months.map((m, index) => (
          <li key={index}>
            <button
              title={m}
              className={cn(
                "btn-block btn text-center",
                state.month === index
                  ? "btn-primary"
                  : state.year === thisYear && index === thisMonth
                  ? "btn-outline border-neutral/40"
                  : "btn-ghost",
              )}
              onClick={() =>
                dispatch({ type: ActionType.SetMonth, payload: index })
              }
            >
              {m}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
});
DatePickerMonthYear.displayName = "DatePickerMonthYear";
